import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { MusicalWork } from "../../../../api/graphql";
import { MusicalWorkListStore } from "../../store";
import { musicalGenreDisplayId } from "../../../musicalGenre/store/MusicalGenreCommonStore";
import { interpreterDisplayId } from "../../../interpreter/store/InterpreterCommonStore";
import { territoryDisplayId } from "../../../territory/store/TerritoryCommonStore";
import { phonogramDisplayId } from "../../../phonogram/store/PhonogramCommonStore";
import { WorkAuthorPanel } from "./WorkAuthorPanel";
import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";
import { TenantSetupGuide } from "../../../tenantProfile/components/common/TenantSetupGuide";

export type GridExpressProps = { listStore?: MusicalWorkListStore };

export const MusicalWorkListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<MusicalWork>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: MusicalWork, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: MusicalWork, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: MusicalWork) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: MusicalWork, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<MusicalWork>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "musicalGenre") {
          return {
            ...col,
            id: "musicalGenre",
            accessorFn: (row: MusicalWork) =>
              row.musicalGenre
                ? musicalGenreDisplayId(row.musicalGenre, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "authors") {
          return {
            ...col,
            id: "authors",
            accessorFn: (row: MusicalWork) =>
              row.authors?.length ? `${row.authors.length} item(s)` : "-",
          };
        }
        if (col.accessorKey === "performers") {
          return {
            ...col,
            id: "performers",
            accessorFn: (row: MusicalWork) =>
              row.performers?.length
                ? row.performers
                    .map((x: any) => interpreterDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "territories") {
          return {
            ...col,
            id: "territories",
            accessorFn: (row: MusicalWork) =>
              row.territories?.length
                ? row.territories
                    .map((x: any) => territoryDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "phonograms") {
          return {
            ...col,
            id: "phonograms",
            accessorFn: (row: MusicalWork) =>
              row.phonograms?.length
                ? row.phonograms
                    .map((x: any) => phonogramDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "createdAt_") {
          return {
            ...col,
            id: "createdAt_",
            accessorFn: (row: MusicalWork) =>
              row.createdAt_
                ? new Date(row.createdAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "lastUpdatedAt_") {
          return {
            ...col,
            id: "lastUpdatedAt_",
            accessorFn: (row: MusicalWork) =>
              row.lastUpdatedAt_
                ? new Date(row.lastUpdatedAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.musicalWorkCurrentPage || [];

    return (
      <Box id={"ListOfMusicalWork"}>
        <TenantSetupGuide />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
          enableExpanding
          renderDetailPanel={({ row }) => (
            <Box sx={{ pl: 4, pr: 2, pb: 2, backgroundColor: "action.hover" }}>
              <WorkAuthorPanel parentId={row.original.id} />
            </Box>
          )}
        />
      </Box>
    );
  }
);
